import { useState, useRef } from "react";
import { Upload, Loader2, Sparkles, Download, RefreshCw, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { STAMP_PATTERNS, STAMP_COLORS, STAMP_COLOR_FAMILIES } from "@/lib/constants";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminVisualizer() {
  const [photo, setPhoto] = useState(null); // { file, preview }
  const [patternSlug, setPatternSlug] = useState(STAMP_PATTERNS[0].slug);
  const [colorSlug, setColorSlug] = useState(STAMP_COLORS[0].slug);
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const pattern = STAMP_PATTERNS.find((p) => p.slug === patternSlug);
  const color = STAMP_COLORS.find((c) => c.slug === colorSlug);

  const pickFile = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file.");
    setPhoto({ file: f, preview: URL.createObjectURL(f) });
    setResult(null);
  };

  const render = async () => {
    if (!photo) return toast.error("Upload a photo first.");
    setRendering(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", photo.file);
      fd.append("pattern", pattern.name);
      fd.append("color_name", color.name);
      fd.append("color_code", color.code);
      fd.append("color_hex", color.hex);
      const { data } = await api.post("/visualizer/render", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult({ ...data, fullUrl: `${BACKEND}${data.url}` });
      toast.success("Render complete!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Render failed.");
    } finally {
      setRendering(false);
    }
  };

  return (
    <div className="max-w-6xl" data-testid="admin-visualizer">
      <div className="flex items-center gap-3 mb-1">
        <span className="h-9 w-9 bg-primary/10 flex items-center justify-center"><Sparkles className="h-5 w-5 text-primary" /></span>
        <h1 className="font-heading text-2xl font-extrabold">AI Concrete Visualizer</h1>
        <span className="text-[10px] font-bold bg-primary/15 text-primary px-2 py-0.5 rounded-sm">ADMIN TEST</span>
      </div>
      <p className="text-muted-foreground mb-6 text-sm">Upload a customer's yard photo, pick a pattern + MasterColor®, and generate a stamped-concrete preview. Admin renders are unlimited.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Controls */}
        <div className="bg-card border border-border p-6 space-y-5">
          <div>
            <Label className="font-semibold">1. Upload a photo</Label>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files); }}
              className="mt-1.5 border-2 border-dashed border-border bg-muted/40 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer rounded-sm p-6 text-center"
              data-testid="viz-upload-zone"
            >
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickFile(e.target.files)} data-testid="viz-file-input" />
              {photo ? (
                <img src={photo.preview} alt="Original" className="max-h-52 mx-auto object-contain rounded-sm" data-testid="viz-original-preview" />
              ) : (
                <>
                  <Upload className="h-6 w-6 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground mt-2">Drag a photo here or <span className="text-primary font-semibold">browse</span></p>
                </>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-semibold">2. Pattern</Label>
              <select value={patternSlug} onChange={(e) => setPatternSlug(e.target.value)} className="mt-1.5 w-full h-10 rounded-sm border border-border bg-background text-sm px-2 focus:outline-none focus:ring-2 focus:ring-primary/40" data-testid="viz-pattern-select">
                {STAMP_PATTERNS.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="font-semibold">3. MasterColor®</Label>
              <select value={colorSlug} onChange={(e) => setColorSlug(e.target.value)} className="mt-1.5 w-full h-10 rounded-sm border border-border bg-background text-sm px-2 focus:outline-none focus:ring-2 focus:ring-primary/40" data-testid="viz-color-select">
                {STAMP_COLOR_FAMILIES.map((fam) => (
                  <optgroup key={fam} label={fam}>
                    {STAMP_COLORS.filter((c) => c.family === fam).map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name} ({c.code})</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="h-6 w-6 rounded-sm ring-1 ring-black/10" style={{ backgroundColor: color.hex }} />
            <span className="font-semibold">{pattern.name}</span>
            <span className="text-muted-foreground">· {color.name} ({color.code})</span>
          </div>

          <Button onClick={render} disabled={rendering || !photo} className="w-full h-12 bg-primary hover:bg-primary/90 rounded-sm font-bold" data-testid="viz-render-btn">
            {rendering ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Generating… (10–30s)</> : <><Sparkles className="h-5 w-5 mr-2" /> Generate Preview</>}
          </Button>
        </div>

        {/* Result */}
        <div className="bg-card border border-border p-6">
          <Label className="font-semibold">Result</Label>
          <div className="mt-1.5 aspect-[4/3] bg-muted/40 border border-border rounded-sm flex items-center justify-center overflow-hidden">
            {rendering ? (
              <div className="text-center text-muted-foreground"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /><p className="text-sm mt-3">Rendering your stamped concrete…</p></div>
            ) : result ? (
              <img src={result.fullUrl} alt="Rendered preview" className="w-full h-full object-contain" data-testid="viz-result-image" />
            ) : (
              <div className="text-center text-muted-foreground"><ImageOff className="h-8 w-8 mx-auto opacity-50" /><p className="text-sm mt-3">Your generated preview will appear here.</p></div>
            )}
          </div>
          {result && (
            <div className="mt-4 flex gap-3">
              <Button asChild variant="outline" className="rounded-sm font-bold" data-testid="viz-download-btn">
                <a href={result.fullUrl} target="_blank" rel="noreferrer" download><Download className="h-4 w-4 mr-2" /> Open / Download</a>
              </Button>
              <Button onClick={render} variant="outline" className="rounded-sm font-bold"><RefreshCw className="h-4 w-4 mr-2" /> Re-render</Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-3">AI previews are approximate. Uses Emergent image AI (small credit cost per render).</p>
        </div>
      </div>
    </div>
  );
}
